<template>
  <CheckoutStep v-if="!hasItems">
    <EmptyCart class="pt-24 md:px-24 md:pt-0 lg:px-0" :items />
  </CheckoutStep>

  <CheckoutStep v-else>
    <template #aside>
      <CheckoutSummary
        :discount
        :discount-token
        :items
        :summary
        :swapOffer
        with-payment-availability
      >
        <template #title>
          {{ i18n(translations.cartSummaryTitle) }}
        </template>
      </CheckoutSummary>
    </template>

    <div aria-live="polite" role="status">
      <CartContent
        :is-loading="loading"
        :items
        :summary
        @next="handleNextStep"
        @update="emit('update')"
        @updating="emit('updating', $event)"
      >
        <Swap
          :items
          :swap-offer
          :total-price-with-swap="summary.totalPriceAfterBuyback"
          zone="swap_cart"
          @updated="emit('update')"
        />

        <CrossSell :items @added="emit('update')" />
      </CartContent>
    </div>
  </CheckoutStep>
</template>

<script setup lang="ts">
import { computed, onMounted, provide } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import CheckoutStep from '@backmarket/nuxt-layer-checkout/components/CheckoutStep/CheckoutStep.vue'
import CheckoutSummary from '@backmarket/nuxt-layer-checkout/components/CheckoutSummary/CheckoutSummary.vue'
import { useCheckoutTracking } from '@backmarket/nuxt-layer-checkout/composables/useCheckoutTracking'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import CartContent from '~/scopes/checkout/components/Cart/components/CartContent/CartContent.vue'
import CrossSell from '~/scopes/checkout/components/Cart/components/CrossSell/CrossSell.vue'
import EmptyCart from '~/scopes/checkout/components/Cart/components/EmptyCart/EmptyCart.vue'
import Swap from '~/scopes/checkout/components/Cart/components/Swap/Swap.vue'
import { useGoNextStep } from '~/scopes/checkout/components/Cart/composables/useGoNextStep'
import type { Discount } from '~/scopes/checkout/types/discount'

import translations from './CartStep.translations'

const { nextStep } = useGoNextStep()
const { trackFunnel } = useCheckoutTracking()

type CartItem = ApiSpecs['/bm/cart']['GET']['response']['cartItems'][number]

const props = defineProps<{
  items: CartItem[]
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  summary: ApiSpecs['/bm/cart']['GET']['response']['summary']
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
  loading: boolean
  paymentMethod: PaymentMethod | null
}>()

const emit = defineEmits<{
  (e: 'update'): void
  (e: 'updating', updatedItem?: CartItem): void
}>()

const i18n = useI18n()

// In this checkout flow, cart is not locked and user are able to update it
provide<boolean>('isCartLocked', false)

const hasItems = computed(() => props.items.length > 0)

function handleNextStep(trackClick?: boolean) {
  nextStep(props.items, { trackClick })
}

onMounted(async () => {
  trackFunnel(props.items, props.swapOffer)
})
</script>
